import View from '@modul-connect/shared/components/atoms/view'
import TextField from '@modul-connect/shared/components/molecules/textField'
import theme from '@modul-connect/shared/theme'
import RangeInput from '@modul-connect/shared/components/molecules/inputfields/rangeInput'
import InfoLabel from '@modul-connect/shared/components/molecules/infoLabel/infoLabel'
import { useEffect, useState } from 'react'
import { Icon } from '@modul-connect/shared/components/atoms/icon'
import { P } from '@modul-connect/shared/components/atoms/text'
import { Dropdown } from '@modul-connect/shared/components/molecules/dropdown/dropdown'
import { SegmentedSelector } from '@modul-connect/shared/components/molecules/segmentedSelector/segmentedSelector'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

export const CreateTemplateModal = ({ openModal, onClose, onSave, disabled, addingFailed, subtrees, templateAlreadyExists }) => {
  if (!openModal) return null

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [emails, setEmails] = useState([])
  const [organizationId, setOrganizationId] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [lowerWarningThreshold, setLowerWarningThreshold] = useState('')
  const [upperWarningThreshold, setUpperWarningThreshold] = useState('')
  const [lowerCriticalThreshold, setLowerCriticalThreshold] = useState('')
  const [upperCriticalThreshold, setUpperCriticalThreshold] = useState('')
  const [emailFrequency, setEmailFrequency] = useState(15) 
  const [daysOfWeek, setDaysOfWeek] = useState([]) 
  const [startTimeLocal, setStartTimeLocal] = useState('00:00')
  const [endTimeLocal, setEndTimeLocal] = useState('00:00')

  const [hasErrorLowerCritical, setHasErrorLowerCritical] = useState(false)
  const [hasErrorUpperCritical, setHasErrorUpperCritical] = useState(false)
  const [hasErrorLowerWarning, setHasErrorLowerWarning] = useState(false)
  const [hasErrorUpperWarning, setHasErrorUpperWarning] = useState(false)
  const [errorEmail, setErrorEmail] = useState(false)
  const [timeRangeError, setTimeRangeError] = useState(false)

  const S = styles()
  const {t} = useTranslation()

  useEffect(() => {
    if (!openModal) return
  
    setName('')
    setEmail('')
    setEmails([])
    setLowerWarningThreshold('')
    setUpperWarningThreshold('')
    setLowerCriticalThreshold('')
    setUpperCriticalThreshold('')
    setEmailFrequency(15)
    setHasErrorLowerCritical(false)
    setHasErrorUpperCritical(false)
    setHasErrorLowerWarning(false)
    setHasErrorUpperWarning(false)
    setTimeRangeError(false)
    setErrorEmail(false)
    if (subtrees.length === 1) {
      setOrganizationId(subtrees[0].uuid)
      setOrganizationName(subtrees[0].name)
    }
  }, [openModal, subtrees])

useEffect(() => {
  if (!startTimeLocal || !endTimeLocal) {
    setTimeRangeError(false)
    return
  }

  const start = DateTime.fromFormat(startTimeLocal, 'HH:mm')
  const end = DateTime.fromFormat(endTimeLocal, 'HH:mm')

  setTimeRangeError(end < start)
}, [startTimeLocal, endTimeLocal])
  const isNumeric = (val) => {
    if (val === null || val === undefined || val === '') return true
    return !isNaN(val)
  }
  const isSaveDisabled =
    !name ||
    !organizationId ||
    !daysOfWeek.length ||
    !startTimeLocal ||
    !endTimeLocal ||
    lowerCriticalThreshold === '' ||
    upperCriticalThreshold === '' ||
    hasErrorLowerCritical ||
    hasErrorUpperCritical ||
    hasErrorLowerWarning ||
    hasErrorUpperWarning ||
    timeRangeError
 
  
  const isValidEmail = (email) => {
    return /^[\w.-]+@[\w.-]+\.\w{2,}$/.test(email)
  }
  const handleAddEmail = () => {
    const formattedEmail = email.trim()

    if(!formattedEmail) return

    if(!isValidEmail(formattedEmail)) {
      setErrorEmail(true)
      return
    }
    if (emails.includes(formattedEmail)) {
      setEmail('')
      return
    }
    setEmails(prev => [...prev, formattedEmail])
    setEmail('')
    setErrorEmail(false)
  }

  const handleRemoveEmail = (emailToRemove) => {
    setEmails(emails.filter(email => email !== emailToRemove))
  }

  const handleSave = () => {
    onSave({
      name,
      organizationId,
      organizationName,
      lowerWarningThreshold: Number(lowerCriticalThreshold),
      upperWarningThreshold: Number(upperCriticalThreshold),
      lowerCriticalThreshold: Number(lowerCriticalThreshold),
      upperCriticalThreshold: Number(upperCriticalThreshold),
      emailFrequency: Number(emailFrequency),
      emails,
      armedSchema: {
        startTimeLocal: startTimeLocal,
        endTimeLocal: endTimeLocal,
        daysOfWeek: daysOfWeek
      }
    })
  }

  const isButtonDisabled = isSaveDisabled || disabled

  const orgOptions = subtrees.map(item => ({
    name: item.name,    
    id: item.uuid          
  }))

  const handleLowerCriticalChange = (val) => {
    setLowerCriticalThreshold(val)
    setHasErrorLowerCritical(!isNumeric(val))
  }
  
  const handleLowerWarningChange = (val) => {
    setLowerWarningThreshold(val)
    setHasErrorLowerWarning(!isNumeric(val))
  }
  
  const handleUpperCriticalChange = (val) => {
    setUpperCriticalThreshold(val)
    setHasErrorUpperCritical(!isNumeric(val))
  }
  
  const handleUpperWarningChange = (val) => {
    setUpperWarningThreshold(val)
    setHasErrorUpperWarning(!isNumeric(val))
  }


  const hasMultipleOrganizations = subtrees.length > 1
  return (
    <View extend={S.background_overlay} onClick={onClose}>
      <View extend={S.modal_container} onClick={e => e.stopPropagation()}>
        <View extend={S.modal_wrapper}>
          {addingFailed && (
            <P large style={{ color: theme.colors.error }}>Adding template failed. (Server error)</P>
          )}
          <View extend={S.modal_column}>
            {hasMultipleOrganizations && (
              <View  extend={{paddingTop: 10, paddingBottom: 24}}>
                <Dropdown
                  options={orgOptions}
                  placeholder={t('climateSystem.settings.template.selectOrganisation')}
                  value={organizationId}
                  onChange={(selectedId) => {
                    setOrganizationId(selectedId)
                
                    const selectedOrg = orgOptions.find(org => org.id === selectedId)
                    if (selectedOrg) {
                      setOrganizationName(selectedOrg.name)
                    }
                  }}
                  isRequired
                />
              </View>
            )}
            <InfoLabel required label={t('climateSystem.settings.template.templateName')}/>
            <TextField
              extend={S.modal_input}
              placeholder={t('climateSystem.settings.template.templateName')}
              value={name}
              icon='Pencil2'
              onChange={e => setName(e.target.value)}
            />
            {templateAlreadyExists && (
           <P noPadding small style={S.error_text }>A template with this name already exists. Please choose a different name.</P>
          )}
          </View>
          <View extend={S.modal_devider}></View>

          <View extend={S.modal_row}>
            <RangeInput    
              hasErrorFromValue={hasErrorLowerCritical}
              hasErrorToValue={hasErrorUpperCritical} 
              errorText={'Must be a number'} 
              fromValue={lowerCriticalThreshold} 
              toValue={upperCriticalThreshold}   
              onChangeFrom={handleLowerCriticalChange} 
              onChangeTo={handleUpperCriticalChange}   
              showInfoText 
              infoText={t('climateSystem.settings.template.tempLimitsInfo')} 
              showInfoIcon 
              required 
              inlineText='°C'
              label={t('climateSystem.settings.template.temperatureLimits')}
            />
            {/* <RangeInput   
              hasErrorFromValue={hasErrorLowerWarning}
              hasErrorToValue={hasErrorUpperWarning}
              errorText={'Must be a number'} 
              fromValue={lowerWarningThreshold} 
              toValue={upperWarningThreshold} 
              onChangeFrom={handleLowerWarningChange} 
              onChangeTo={handleUpperWarningChange}   
              showInfoText 
              infoText={t('climateSystem.settings.template.preWarningInfo')} 
              showInfoIcon 
              inlineText='°C'
              label={t('climateSystem.settings.template.preWarning')}
            /> */}
          </View>

          <View extend={S.modal_devider}></View> 

          <InfoLabel 
            bold showInfoIcon 
            required
            showInfoText 
            infoText={t('climateSystem.settings.template.notificationStartInfo')} 
            label={t('climateSystem.settings.template.notificationAndReportSchedule')}/>
          <View extend={S.modal_row}>
            <RangeInput    
              type='time'
              step='300'
              hasErrorToValue={timeRangeError}
              errorText={'End time must be later than start time.'} 
              fromValue={startTimeLocal} 
              toValue={endTimeLocal}   
              onChangeFrom={setStartTimeLocal} 
              onChangeTo={setEndTimeLocal}   
              label={t('climateSystem.settings.template.time')}
            />
            <View extend={[S.modal_column, ]}>         
              <InfoLabel label={t('climateSystem.settings.template.days')}/>
              <SegmentedSelector
                options={[
                  { label: t('climateSystem.settings.template.mon'), value: 'Monday' },
                  { label: t('climateSystem.settings.template.tue'), value: 'Tuesday' },
                  { label: t('climateSystem.settings.template.wed'), value: 'Wednesday' },
                  { label: t('climateSystem.settings.template.thu'), value: 'Thursday' },
                  { label: t('climateSystem.settings.template.fri'), value: 'Friday' },
                  { label: t('climateSystem.settings.template.sat'), value: 'Saturday' },
                  { label: t('climateSystem.settings.template.sun'), value: 'Sunday' },
                ]}
                value={daysOfWeek}
                onChange={setDaysOfWeek}
                selectMultiple={true}
                extend={{
                  group: { width: 336 },
                  option: { width: 48 }
                }}
              />
            </View>
          </View>  

          <View extend={S.modal_devider}></View>

          <InfoLabel 
            bold 
            showInfoIcon 
            showInfoText 
            infoText={t('climateSystem.settings.template.emailInfo')} 
            label={t('climateSystem.settings.template.emailNotification')}
          />
          <View extend={S.modal_row}>
            <View extend={[S.modal_column, { width: 276, marginBottom: 50 }]}>         
              <InfoLabel label={t('climateSystem.settings.template.emails')}/>
              <TextField 
                value={email} 
                onChange={e => {
                  const val = e.target.value
                  setEmail(val)

                  if (val.trim() === '') {
                    setErrorEmail(false)
                  }}} 
                placeholder={t('climateSystem.settings.template.typeEmailManually')}
                extend={S.modal_input} 
                button={<span onClick={handleAddEmail}>+ {t('climateSystem.settings.template.add')}</span>}/>

              {errorEmail && (
                <P noPadding small style={{ color: theme.colors.error }}>This email is invalid.</P>
              )}
              {emails.length > 0 && (
                <View extend={S.email_list}>
                  {emails.map(item => (
                    <View extend={S.email_item} key={item}>
                      <span>{item}</span>
                      <View onClick={() => handleRemoveEmail(item)}>
                        <Icon onChangeFrom icon={'Delete'}/>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>

            <View extend={[S.modal_column, { width: 276 }]}>         
              <InfoLabel 
                showInfoIcon 
                showInfoText 
                infoText={t('climateSystem.settings.template.sendAlertAfterInfo')} 
                label={t('climateSystem.settings.template.sendAlertAfter')}/>

              <SegmentedSelector
                options={[
                  { label: '15 min', value: 15 },
                  { label: '30 min', value: 30 },
                  { label: '60 min', value: 60 },
                ]}
                value={emailFrequency}
                onChange={setEmailFrequency}
              />
            </View>
          </View>
        </View>

        <View extend={S.modal_footer}>
          <View extend={S.modal_buttons}>
            <View onClick={!isButtonDisabled ? handleSave : undefined} extend={S.save_button({ disabled: isButtonDisabled})}>
              <View  extend={() => S.button_text({ isCancel: false })}>
              {t('save')}
              </View>
            </View>
            <View extend={S.cancel_button} onClick={onClose}>
              <View extend={() => S.button_text({ isCancel: true })}>
              {t('cancel')}
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  )
}

const styles = () => ({
  background_overlay: () => ({
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.36)',
    zIndex: 999,
  }),

  modal_container: () => ({
    display: 'flex',
    flexDirection: 'column',
    padding: '32px 12px 32px 24px',
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    left: '50%',
    top: '50%',
    width: 651,
    height: 571,
    background: '#FFFFFF',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.08)',
    borderRadius: 12,
    zIndex: 10,
  }),
  modal_column: () => ({
    display: 'flex',
    flexDirection: 'column', 
    gap: 8
  }),
  modal_row: () => ({
    display: 'flex', 
    flexDirection: 'row', 
    gap: 24
  }),
  modal_wrapper: () => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    paddingRight: 40,
    width: '100%',
    height: 512,
    overflowY: 'auto',
  }),

  modal_input: () => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '8px 12px',
    gap: 16,
    width: '100%',
    height: 35,
    border: '1px solid #CACACA',
    borderRadius: 4,
    backgroundColor: 'transparent',
    marginBottom: 0,
    
  }),
  label_text: () => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontSize: 12,
    lineHeight: '15px',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    
  }),

  required_mark: {
    color: '#D32F2F',
    marginLeft: 2,
  },

  modal_footer: () => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: '36px 0px 24px',
    gap: 24,
  }),

  modal_buttons: () => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  }),

  save_button: ({ disabled }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '8px 66px',
    gap: 10,
    width: 169,
    height: 35,
    background: disabled ? '#494949' : theme.colors.buttonBackground,
    borderRadius: 4,
    cursor: disabled ? 'default' : 'pointer',
  }),

  button_text: ({ isCancel }) => ({
    width: isCancel ? 53 : 37,
    height: 19,
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '19px',
    color: isCancel ? '#404040' : '#FDFDFD',
  }),

  cancel_button: () => ({
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '8px 20px',
    gap: 12,
    width: 93,
    height: 35,
    border: '1px solid #404040',
    borderRadius: 4,
    cursor: 'pointer',
  }),
  modal_devider: () => ({
    width: '100%',
    border: '1px solid #CACACA',

  }),
  email_list: () => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  }),
  email_item: () => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    cursor: 'pointer'
  }),
  error_text: () => ({
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontSize: 12,
    color: theme.colors.error
  })

})